import { isApiError, isMissingSessionError } from "@/lib/axios";

/** The client `retry: 1` default this restores for everything that IS worth retrying. */
const DEFAULT_RETRY_COUNT = 1;

/**
 * Retry policy for the agency reads: never retry a 4xx.
 *
 * The `QueryClient` default in `src/routes/__root.tsx` is a flat `retry: 1`,
 * which re-attempts a client error that will answer identically the second
 * time. That is not merely wasted — it fights the response interceptor in
 * `lib/axios.ts`, which on **401** clears `sessionJwt` and `sessionToken` and
 * then assigns `window.location.href = "/login"`. A retried 401 fires that
 * navigation a second time, and `useAgencyClients` polls every 3s while a
 * disconnect is in flight, so it would fire on every tick.
 *
 * A 4xx is a verdict, not a blip. 5xx and transport failures (no response at
 * all — timeout, DNS, offline) keep the default single retry, because those
 * are the ones a second attempt can genuinely win.
 *
 * Mirrors `apps/venue-app/src/hooks/marketing/trips/queryRetry.ts`, which
 * solved the same problem on the venue side.
 *
 * Pass as `retry` on a query. Mutations are deliberately not covered: a
 * connect or a disconnect must never be re-fired automatically.
 */
export const retryUnlessClientError = (
  failureCount: number,
  error: unknown,
): boolean => {
  // The request interceptor refused to send this one because there is no
  // `sessionJwt`. Nothing reached the network and a second attempt would be
  // refused identically — it is the same verdict as a 401, reached earlier.
  if (isMissingSessionError(error)) {
    return false;
  }

  const status = isApiError(error) ? error.statusCode : undefined;

  if (typeof status === "number" && status >= 400 && status < 500) {
    return false;
  }

  return failureCount < DEFAULT_RETRY_COUNT;
};
